import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Button, CircularProgress, Typography } from '@mui/material'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { CheckboxWithLabel, TextField } from 'formik-mui'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { useOrderOrigins, useProductOrigins } from '../../../hooks'
import { OrderOrigin } from '../../../interfaces/OrderOrigin'
import { ProductOrigin } from '../../../interfaces/Producto/ProductOrigins'
import { ProductExternalReference } from '../../../interfaces/Producto/ProductExternalReference'
import { GenericMultiselectDLL } from '../../../components/Almacen/UI/GenericMultiselectDLL'
import { FormFieldSection } from '../../../components/_EOS/FormFieldSection'
import {
  ModalScrollableContent,
  fillFormStyle
} from '../../../components/_EOS/Modal'
import { RequiredMark } from '../../ProductOrigin/Components'
import { ApiResponse } from '../../../typings/ApiResponse'
import { getProductExternalReferences } from '../../ProductExternalReference/config/api'
import {
  createProductOriginTechnical,
  ProductOriginTechnicalPayload,
  updateProductOriginTechnical
} from '../config/api'
import { productOriginTechnicalValidationSchema } from '../types'

interface CreateEditProductOriginTechnicalModalProps {
  productOrigin?: ProductOrigin
  isEditing?: boolean
  closeModal: () => void
  onSuccess: () => void
  handleSnackbar: (message: string, success: boolean) => void
}

interface FormValues {
  productOriginId: number
  origin: string
  name: string
  isActive: boolean
}

const initialValues: FormValues = {
  productOriginId: 0,
  origin: '',
  name: '',
  isActive: true
}

export const CreateEditProductOriginTechnicalModal: React.FC<
  CreateEditProductOriginTechnicalModalProps
> = ({
  productOrigin,
  isEditing = false,
  closeModal,
  onSuccess,
  handleSnackbar
}) => {
  const { callApi, state } = useApi()

  const { getProductOrigins } = useProductOrigins()

  const {
    getOrderOrigins,
    orderOriginsState: { orderOrigins }
  } = useOrderOrigins()

  useEffect(() => {
    if (orderOrigins?.length === 0) {
      getOrderOrigins()
    }
  }, [])

  const {
    callApi: getExternalReferencesCallApi,
    state: externalReferencesState
  } = useApi<ApiResponse<ProductExternalReference>>()

  useEffect(() => {
    getExternalReferencesCallApi(
      getProductExternalReferences(
        [
          {
            key: 'isActive',
            value: 'true',
            label: 'Activo',
            displayValue: 'true'
          }
        ],
        0,
        50,
        { field: 'name', direction: 'asc' }
      )
    ).catch(() => undefined)
  }, [])

  const [selectedOrderOriginIds, setSelectedOrderOriginIds] = useState<
    number[]
  >(productOrigin?.orderOriginIds ? [...productOrigin.orderOriginIds] : [])

  const [selectedExternalReferenceIds, setSelectedExternalReferenceIds] =
    useState<number[]>(
      productOrigin?.productOriginsExternalReferences?.map(
        (reference) => reference.productReferenceId
      ) || []
    )

  const handleSubmit = (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    if (selectedOrderOriginIds.length === 0) {
      setSubmitting(false)
      return
    }

    const payload: ProductOriginTechnicalPayload = {
      productOriginId: values.productOriginId,
      origin: values.origin.trim(),
      name: values.name.trim(),
      orderOriginIds: selectedOrderOriginIds,
      productExternalReferencesIds: selectedExternalReferenceIds,
      isActive: values.isActive
    }

    const request = isEditing
      ? updateProductOriginTechnical(values.productOriginId, payload)
      : createProductOriginTechnical(payload)

    callApi(request)
      .then(() => {
        const message = isEditing
          ? 'Marca actualizada correctamente'
          : 'Marca creada correctamente'
        handleSnackbar(message, true)
        getProductOrigins()
        onSuccess()
        closeModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
        setSubmitting(false)
      })
  }

  // Es el tema compacto que inyecta el Modal, no el de la app: hay que partir
  // de el para no perder la densidad del resto de los controles.
  const compactTheme = useTheme()

  // El modal corre con `compact`, que baja toda la tipografia a 0.85: el texto
  // de ayuda y el titulo de cada FormFieldSection (body2 / subtitle2, 0.875rem)
  // terminaban en ~11.9px. Este tema anidado los sube apenas, y solo para el
  // subarbol de este formulario — FormFieldSection es un primitivo de _EOS/,
  // asi que el tamano no se toca ahi para no arrastrar a otras pantallas.
  const fieldTextTheme = useMemo(
    () =>
      createTheme(compactTheme, {
        typography: {
          body2: { fontSize: '0.8rem', lineHeight: 1.4 },
          subtitle2: { fontSize: '0.8rem' }
        }
      }),
    [compactTheme]
  )

  const normalizedInitialValues: FormValues = productOrigin
    ? {
        productOriginId: productOrigin.productOriginId,
        origin: productOrigin.origin,
        name: productOrigin.name,
        isActive: productOrigin.isActive ?? true
      }
    : initialValues

  return (
    <Formik
      initialValues={normalizedInitialValues}
      validationSchema={productOriginTechnicalValidationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, submitCount }) => (
        <Form autoComplete='off' style={fillFormStyle}>
          <ModalScrollableContent
            fill
            actions={
              <Fragment>
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={closeModal}
                  disabled={isSubmitting}
                  sx={{ borderRadius: '12px' }}
                >
                  Cancelar
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={isSubmitting}
                  sx={{ borderRadius: '12px' }}
                  startIcon={
                    state.status === Status.Loading ? (
                      <CircularProgress size={20} color='inherit' />
                    ) : undefined
                  }
                >
                  {state.status === Status.Loading
                    ? 'Procesando...'
                    : 'Guardar'}
                </Button>
              </Fragment>
            }
          >
            <Typography variant='caption' color='text.secondary'>
              Los campos marcados con <RequiredMark /> son obligatorios.
            </Typography>

            <ThemeProvider theme={fieldTextTheme}>
              <FormFieldSection
                description='Puede seleccionar uno o más canales de venta previamente creados en el mantenedor de Canales de Venta.'
                label={
                  <>
                    Canal de venta (ClientAlias)
                    <RequiredMark />
                  </>
                }
              >
                <GenericMultiselectDLL<OrderOrigin>
                  initialValues={orderOrigins || []}
                  aria-label='Canal de venta (ClientAlias)'
                  title='canal de venta'
                  name='origin'
                  chipName='origin'
                  valueKey='orderOriginId'
                  selectedItems={selectedOrderOriginIds}
                  setSelectedItems={setSelectedOrderOriginIds}
                  fullWidth
                  variant='outlined'
                  size='small'
                  disabled={isSubmitting}
                  error={submitCount > 0 && selectedOrderOriginIds.length === 0}
                  helperText={
                    submitCount > 0 && selectedOrderOriginIds.length === 0
                      ? 'Debe seleccionar al menos un canal de venta.'
                      : undefined
                  }
                />
              </FormFieldSection>

              <FormFieldSection
                description='Identificador técnico utilizado por el WMS para comunicarse con sistemas externos.'
                label={
                  <>
                    Origen del producto
                    <RequiredMark />
                  </>
                }
              >
                <Field
                  fullWidth
                  name='origin'
                  component={TextField}
                  variant='outlined'
                  size='small'
                  disabled={isSubmitting}
                  inputProps={{
                    'aria-required': true,
                    'aria-label': 'Origen del producto'
                  }}
                />
              </FormFieldSection>

              <FormFieldSection
                description='Identificador utilizado por PIM para relacionar con la marca dentro del WMS.'
                label='Referencia Externa (Marca PIM)'
              >
                <GenericMultiselectDLL<ProductExternalReference>
                  initialValues={externalReferencesState.data?.items || []}
                  aria-label='Referencia Externa (Marca PIM)'
                  title='referencia externa'
                  name='name'
                  chipName='name'
                  valueKey='productExternalReferenceId'
                  selectedItems={selectedExternalReferenceIds}
                  setSelectedItems={setSelectedExternalReferenceIds}
                  fullWidth
                  variant='outlined'
                  size='small'
                  disabled={isSubmitting}
                />
              </FormFieldSection>

              <FormFieldSection
                description='Nombre con el que la marca será identificada dentro del WMS.'
                label={
                  <>
                    Nombre Marca en WMS
                    <RequiredMark />
                  </>
                }
              >
                <Field
                  fullWidth
                  name='name'
                  component={TextField}
                  variant='outlined'
                  size='small'
                  disabled={isSubmitting}
                  inputProps={{
                    'aria-required': true,
                    'aria-label': 'Nombre Marca en WMS'
                  }}
                />
              </FormFieldSection>

              <FormFieldSection label='Estado de la Marca'>
                <Field
                  name='isActive'
                  component={CheckboxWithLabel}
                  type='checkbox'
                  color='primary'
                  size='small'
                  Label={{ label: 'Activo' }}
                  disabled={isSubmitting}
                />
              </FormFieldSection>
            </ThemeProvider>
          </ModalScrollableContent>
        </Form>
      )}
    </Formik>
  )
}
